<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import ExperienceRow from './ExperienceRow.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const CREATIVE_ADJECTIVES = [
  'Brilliant',
  'Electric',
  'Golden',
  'Midnight',
  'Silver',
  'Vibrant',
  'Cosmic',
  'Radiant',
  'Dynamic',
  'Velvet',
];
const CREATIVE_NOUNS = [
  'Workshop',
  'Gala',
  'Summit',
  'Gathering',
  'Session',
  'Retreat',
  'Journey',
  'Showcase',
  'Forum',
  'Affair',
];
const randomCreativeName = () => {
  const adj =
    CREATIVE_ADJECTIVES[Math.floor(Math.random() * CREATIVE_ADJECTIVES.length)];
  const noun =
    CREATIVE_NOUNS[Math.floor(Math.random() * CREATIVE_NOUNS.length)];
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${adj} ${noun} ${suffix}`;
};

const creating = ref(false);

const loading = computed(
  () => store.getters['planningExperiences/getUIFlags']?.isFetching || false
);
const experiences = computed(
  () => store.getters['planningExperiences/getRecords'] || []
);

const selectedExperience = ref(null);
const showDeleteModal = ref(false);
const deletingLoading = ref({});

onMounted(async () => {
  await store.dispatch('planningExperiences/get');
});

const handleCreate = async () => {
  creating.value = true;
  try {
    const experience = await store.dispatch('planningExperiences/create', {
      name: randomCreativeName(),
    });
    router.push({
      name: 'planning_experiences_edit',
      params: { experienceId: experience.id },
    });
  } catch (error) {
    const apiMessage = error?.response?.data?.message;
    useAlert(apiMessage || t('PLANNING_EXPERIENCES.ADD.API.ERROR_MESSAGE'));
  } finally {
    creating.value = false;
  }
};

const handleEdit = experience => {
  router.push({
    name: 'planning_experiences_edit',
    params: { experienceId: experience.id },
  });
};

const handleDelete = experience => {
  selectedExperience.value = experience;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  if (!selectedExperience.value) return;

  deletingLoading.value[selectedExperience.value.id] = true;
  try {
    await store.dispatch(
      'planningExperiences/delete',
      selectedExperience.value.id
    );
    useAlert(t('PLANNING_EXPERIENCES.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('PLANNING_EXPERIENCES.DELETE.API.ERROR_MESSAGE'));
  } finally {
    deletingLoading.value[selectedExperience.value.id] = false;
    showDeleteModal.value = false;
    selectedExperience.value = null;
  }
};

const cancelDelete = () => {
  showDeleteModal.value = false;
  selectedExperience.value = null;
};

const tableHeaders = computed(() => {
  return [
    t('PLANNING_EXPERIENCES.LIST.TABLE_HEADER.NAME'),
    t('PLANNING_EXPERIENCES.LIST.TABLE_HEADER.PRICE'),
    t('PLANNING_EXPERIENCES.LIST.TABLE_HEADER.STATUS'),
    '',
  ];
});
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('PLANNING_EXPERIENCES.LIST.LOADING')"
    :no-records-found="!experiences.length"
    :no-records-message="$t('PLANNING_EXPERIENCES.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('PLANNING_EXPERIENCES.HEADER')"
        :description="$t('PLANNING_EXPERIENCES.FORM.SUBTITLE')"
      >
        <template #actions>
          <Button
            icon="i-lucide-plus"
            md
            :label="$t('PLANNING_EXPERIENCES.ADD.TITLE')"
            :is-loading="creating"
            @click="handleCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 ltr:pr-4 rtl:pl-4 rtl:text-right ltr:text-left font-semibold text-n-slate-11 text-sm"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <ExperienceRow
            v-for="experience in experiences"
            :key="experience.id"
            :experience="experience"
            :loading="deletingLoading[experience.id]"
            @edit="handleEdit"
            @delete="handleDelete"
          />
        </tbody>
      </table>
    </template>

    <!-- Delete Confirmation Modal -->
    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-confirm="confirmDelete"
      :title="$t('PLANNING_EXPERIENCES.DELETE.CONFIRM.TITLE')"
      :message="$t('PLANNING_EXPERIENCES.DELETE.CONFIRM.MESSAGE')"
      :message-value="selectedExperience?.name"
      :confirm-text="$t('PLANNING_EXPERIENCES.DELETE.CONFIRM.YES')"
      :reject-text="$t('PLANNING_EXPERIENCES.DELETE.CONFIRM.NO')"
      @close="cancelDelete"
    />
  </SettingsLayout>
</template>
